import { IconPicker, MultilineInput, type IconPickerOption } from '@amelio/ui-library';

import { Trash2Icon } from '../icons';
import { LevelIconPreview } from './LevelIconPreview';

import type { PerformanceScoreLevel } from '@/types/competency/PerformanceScoreLevel';

import LevelColorPicker from '@/components/LevelColorPicker/LevelColorPicker';
import { useAmelioTranslations } from '@/hooks';
import { showErrorToast } from '@/plugins/Toast';
import { getEditableTextByLanguage } from '@/utils/localization';

const MAX_TITLE_LENGTH = 100;
const MAX_DESCRIPTION_LENGTH = 500;

const preventNewline = () => undefined;

export type PerformanceScoreLevelErrorMessages = {
    title?: string;
    description?: string;
    minScore?: string;
    icon?: string;
    color?: string;
};

type PerformanceScoreLevelCardProps = {
    index: number;
    level: PerformanceScoreLevel;
    language: string;
    iconOptions: IconPickerOption[];
    errorMessages?: PerformanceScoreLevelErrorMessages;
    canRemove: boolean;
    onUpdate: (index: number, patch: Partial<PerformanceScoreLevel>) => void;
    onRemove: (index: number) => void;
};

export const PerformanceScoreLevelCard = ({
    index,
    level,
    language,
    iconOptions,
    errorMessages,
    canRemove,
    onUpdate,
    onRemove,
}: PerformanceScoreLevelCardProps) => {
    const { t } = useAmelioTranslations();

    const title = getEditableTextByLanguage(level.label, language);
    const description = getEditableTextByLanguage(level.description, language);

    const updateTitle = (value: string) => {
        const singleLineValue = value.replace(/\r\n|\r|\n/g, ' ');

        if (singleLineValue.length > MAX_TITLE_LENGTH) {
            showErrorToast({ message: t('competencies.levels.nameTooLong') });
            return;
        }
        onUpdate(index, {
            label: { ...level.label, [language]: singleLineValue },
        });
    };

    const updateDescription = (value: string) => {
        if (value.length > MAX_DESCRIPTION_LENGTH) {
            showErrorToast({ message: t('competencies.levels.descriptionTooLong') });
            return;
        }
        onUpdate(index, {
            description: { ...level.description, [language]: value },
        });
    };

    return (
        <div
            className={
                'group border-base-100 flex items-stretch gap-4 overflow-hidden rounded-2xl border bg-white/70 backdrop-blur-md transition-shadow hover:shadow-lg'
            }
        >
            <div
                className={
                    'from-primary/10 to-purple/10 border-base-100 flex w-24 shrink-0 flex-col items-center justify-center gap-1 border-r bg-gradient-to-b py-3'
                }
            >
                <span className={'text-navy text-4xl leading-none font-bold'}>{index + 1}</span>

                <span className={'text-base-400 text-xs tracking-wider uppercase'}>
                    {t('competencies.performanceScoreLevels.tier')}
                </span>
            </div>

            <div className={'flex flex-1 items-start gap-3.5 py-6 pr-6 pl-1'}>
                <IconPicker
                    options={iconOptions}
                    value={level.icon}
                    selectedPreview={
                        <LevelIconPreview
                            icon={level.icon}
                            iconColor={level.color}
                            className={'h-5 w-5'}
                        />
                    }
                    onSelect={(icon) => onUpdate(index, { icon })}
                    className={'!h-[40px] !w-14 shrink-0 !border-gray-200'}
                />

                <LevelColorPicker
                    value={level.color}
                    onChange={(color) => onUpdate(index, { color })}
                />

                <div className={'min-w-0 flex-1 space-y-2'}>
                    <MultilineInput
                        value={title}
                        placeholder={t('competencies.performanceScoreLevels.titlePlaceholder')}
                        onChange={updateTitle}
                        onEnterPress={preventNewline}
                        variant={'white'}
                        rows={1}
                        autoGrow
                        error={!!errorMessages?.title}
                        errorMessage={errorMessages?.title}
                        className={
                            'text-navy text-body-sm h-auto min-h-0 border-0 bg-transparent px-0 py-0.5 font-semibold wrap-break-word'
                        }
                        dataTestId={`performance-level-title-${index}`}
                    />

                    <MultilineInput
                        value={description}
                        placeholder={t(
                            'competencies.performanceScoreLevels.descriptionPlaceholder',
                        )}
                        onChange={updateDescription}
                        variant={'white'}
                        rows={2}
                        autoGrow
                        error={!!errorMessages?.description}
                        errorMessage={errorMessages?.description}
                        className={
                            'h-auto min-h-0 border-0 bg-transparent px-0 py-0.5 text-sm leading-normal wrap-break-word text-gray-500'
                        }
                        dataTestId={`performance-level-description-${index}`}
                    />
                </div>

                <button
                    type={'button'}
                    onClick={() => onRemove(index)}
                    disabled={!canRemove}
                    aria-label={t('competencies.performanceScoreLevels.remove')}
                    className={
                        'text-base-400 hover:bg-error/10 hover:text-error flex h-9 w-9 shrink-0 items-center justify-center rounded-lg opacity-0 transition-colors group-hover:opacity-100 disabled:cursor-not-allowed disabled:opacity-0'
                    }
                    data-testid={`performance-level-remove-${index}`}
                >
                    <Trash2Icon className={'h-4 w-4'} />
                </button>
            </div>
        </div>
    );
};
